import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { FC } from "react";

import { Container } from "../../container";
import { ContentfulImage } from "../../contentful-image";
import { BannerCardsItem } from "./banner-cards-item";

export interface BannerCardsProps extends Queries.BannerCardsDataFragment {}

export const BannerCards: FC<BannerCardsProps> = ({
  shouldFlexCards,
  primaryCard,
  secondaryCard,
  backgroundImage,
  backgroundColor = "neutral",
  title,
  description,
  logo,
}) => {
  const isAppendix = [primaryCard, secondaryCard]
    .filter(Boolean)
    .reduce((acc, card) => acc && card?.__typename === "ContentfulAppendixCard", true);

  return (
    <div
      className={clsx(
        "section-overlap-bottom z-30 overflow-hidden relative w-full",
        "py-10 lg:pt-20 lg:pb-24",
        {
          "bg-neutral": backgroundColor === "neutral",
          "bg-light": backgroundColor === "light",
          "bg-primary": backgroundColor === "primary",
        }
      )}
    >
      {(title || description || logo) && (
        <Container pagePaddingX containerClassName="flex z-10 flex-col text-center gap-6 mb-12">
          {logo && (
            <ContentfulImage
              image={logo}
              className="max-h-36 !w-full [&_img]:mx-auto [&_img]:w-fit [&_img]:h-full"
              gatsbyImageProps={{ objectFit: "contain" }}
            />
          )}
          {title && (
            <h2
              className={clsx(
                "text-4xl 2xl:text-6xl font-medium md:font-bold",
                backgroundColor === "primary" ? "text-light" : "text-primary"
              )}
            >
              {title}
            </h2>
          )}
          {description && (
            <div className="text-lg xl:text-xl 2xl:text-2xl">{description?.text}</div>
          )}
        </Container>
      )}
      <Container
        pagePaddingX
        className="z-10 relative"
        containerClassName={clsx(
          "flex flex-col lg:flex-row gap-5 lg:gap-10 items-start",
          shouldFlexCards ? "justify-between" : "justify-center"
        )}
      >
        {primaryCard && (
          <BannerCardsItem
            cardProps={primaryCard}
            className={clsx(
              shouldFlexCards
                ? secondaryCard && isAppendix
                  ? "flex-[1_1_60%]"
                  : "flex-[1_1_50%]"
                : undefined
            )}
          />
        )}
        {secondaryCard && (
          <BannerCardsItem
            cardProps={secondaryCard}
            className={clsx(
              shouldFlexCards ? (isAppendix ? "flex-[1_1_40%]" : "flex-[1_1_50%]") : undefined
            )}
          />
        )}
      </Container>
      {backgroundImage?.gatsbyImageData && (
        <div className="absolute w-full h-full top-0 left-0">
          <GatsbyImage
            image={backgroundImage?.gatsbyImageData}
            alt={primaryCard && "title" in primaryCard ? primaryCard.title ?? "" : ""}
            objectFit="cover"
            className="w-full h-full"
          />
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment BannerCardsData on ContentfulBannerCards {
    shouldFlexCards
    logo {
      ...ContentfulFixedImageData
    }
    primaryCard {
      ... on ContentfulAppendixCard {
        ...AppendixCardData
      }
      ... on ContentfulImageCard {
        ...ImageCardData
      }
      ... on ContentfulMaterialCard {
        ...MaterialCardData
      }
    }
    secondaryCard {
      ... on ContentfulAppendixCard {
        ...AppendixCardData
      }
      ... on ContentfulImageCard {
        ...ImageCardData
      }
      ... on ContentfulMaterialCard {
        ...MaterialCardData
      }
    }
    backgroundImage {
      gatsbyImageData
    }
    backgroundColor
    title
    description {
      text: description
    }
  }
`;
